import { useState } from "react";
import { toast } from "sonner";
import { Save, Send, X } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { CATEGORIES } from "@/lib/categories";

export default function ArticleEditor({ article, onSaved, onCancel }) {
  const { tr } = useLang();
  const a = tr("admin");
  const isEdit = Boolean(article && article.id);
  const wasPublished = article?.status === "published";

  const [section, setSection] = useState(article?.section || "insights");
  const [category, setCategory] = useState(article?.category || "INSIGHT");
  const [it, setIt] = useState({ title: article?.it?.title || "", excerpt: article?.it?.excerpt || "", body: article?.it?.body || "" });
  const [en, setEn] = useState({ title: article?.en?.title || "", excerpt: article?.en?.excerpt || "", body: article?.en?.body || "" });
  const [busy, setBusy] = useState(false);

  const inputCls =
    "w-full border border-white/15 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/30 transition-colors focus:border-gold/60 focus:outline-none";
  const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-gold";

  const save = async (publish) => {
    if (!it.title.trim() || !en.title.trim() || !it.body.trim() || !en.body.trim()) {
      toast.error("Titolo e testo sono obbligatori in IT ed EN / Title and body required in IT and EN");
      return;
    }
    setBusy(true);
    try {
      const payload = { section, category: (category || "INSIGHT").toUpperCase(), publish: publish || wasPublished, it, en };
      const { data } = isEdit
        ? await api.put(`/admin/articles/${article.id}`, payload)
        : await api.post("/admin/articles", payload);
      toast.success(publish && !wasPublished ? a.published : a.saved);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-article-editor">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-ivory" data-testid="editor-heading">
          {isEdit ? a.editorTitle.edit : a.editorTitle.create}
        </h2>
        <button onClick={onCancel} data-testid="editor-cancel-button" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-ivory/60 transition-colors hover:border-gold/50 hover:text-gold-bright">
          <X className="h-3.5 w-3.5" /> {a.cancel}
        </button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <label htmlFor="editor-section" className={labelCls}>{a.section}</label>
          <select id="editor-section" data-testid="editor-section-select" className={inputCls} value={section} onChange={(e) => setSection(e.target.value)}>
            <option value="knowledge">{a.sectionKnowledge}</option>
            <option value="insights">{a.sectionInsights}</option>
          </select>
        </div>
        <div>
          <label htmlFor="editor-category" className={labelCls}>{a.category}</label>
          <input id="editor-category" data-testid="editor-category-input" list="editor-categories" className={inputCls} value={category} onChange={(e) => setCategory(e.target.value.toUpperCase())} />
          <datalist id="editor-categories">
            {CATEGORIES.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
      </div>

      <div className="mt-6 grid gap-8 md:grid-cols-2">
        {(["it", "en"]).map((loc) => {
          const val = loc === "it" ? it : en;
          const set = loc === "it" ? setIt : setEn;
          return (
            <div key={loc} className="border border-white/10 bg-white/[0.02] p-5">
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-gold-bright">{loc.toUpperCase()}</p>
              <div className="space-y-4">
                <div>
                  <label htmlFor={`editor-title-${loc}`} className={labelCls}>{loc === "it" ? a.fieldTitleIt : a.fieldTitleEn}</label>
                  <input id={`editor-title-${loc}`} data-testid={`editor-title-${loc}`} className={inputCls} value={val.title} onChange={(e) => set({ ...val, title: e.target.value })} />
                </div>
                <div>
                  <label htmlFor={`editor-excerpt-${loc}`} className={labelCls}>{loc === "it" ? a.fieldExcerptIt : a.fieldExcerptEn}</label>
                  <textarea id={`editor-excerpt-${loc}`} data-testid={`editor-excerpt-${loc}`} rows={2} className={`${inputCls} resize-y`} value={val.excerpt} onChange={(e) => set({ ...val, excerpt: e.target.value })} />
                </div>
                <div>
                  <label htmlFor={`editor-body-${loc}`} className={labelCls}>{loc === "it" ? a.fieldBodyIt : a.fieldBodyEn}</label>
                  <textarea id={`editor-body-${loc}`} data-testid={`editor-body-${loc}`} rows={12} className={`${inputCls} resize-y font-mono text-[13px] leading-relaxed`} value={val.body} onChange={(e) => set({ ...val, body: e.target.value })} />
                  <p className="mt-2 text-[11px] text-ivory/35">{a.fieldBodyHint}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex flex-wrap gap-4 border-t border-gold/15 pt-8">
        <button
          onClick={() => save(false)}
          data-testid="save-article-button"
          disabled={busy}
          className="inline-flex items-center gap-2 border border-gold/50 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-gold-bright transition-colors hover:bg-gold/10 disabled:opacity-50"
        >
          <Save className="h-4 w-4" /> {a.save}
        </button>
        <button
          onClick={() => save(true)}
          data-testid="save-publish-article-button"
          disabled={busy}
          className="inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-gold-bright disabled:opacity-50"
        >
          <Send className="h-4 w-4" /> {a.savePublish}
        </button>
      </div>
    </div>
  );
}
